import { Dialog, FileUpload, Portal, Progress } from "@ark-ui/react";
import {
  DELETION_FLAG,
  FIM_ITEM,
  INDEPENDENCE_LEVEL_TYPE,
  SimpleIndependenceLevelBatchRegistrationHeaderRows,
  SimpleIndependenceLevelBatchRegistrationRowSchema,
  SimpleIndependenceLevels1,
  UserKey,
  toSimpleIndependenceLevelBatchRegistrationRow,
} from "@packages/core";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import Encoding from "encoding-japanese";
import { useEffect, useMemo, useRef, useState } from "react";
import { LuDownload, LuFile, LuTrash } from "react-icons/lu";
import { EmptyError, filter, from, lastValueFrom, map, mergeMap, toArray } from "rxjs";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import {
  ButtonStyle,
  DialogStyle,
  FileUploadStyle,
  PageName,
  PaperStyle,
  TypographyStyle,
  progressStyles,
  showMessage,
} from "#components";
import {
  registerIndependenceLevels,
  removeIndependenceLevelEntryInfiniteQueryCache,
  saveAsFile,
  useFacilityQuery,
  useResidentInfiniteQuery,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/simple-independence-levels/batch-registration",
)({ component: Component });

const fileUploadStyle = FileUploadStyle();

function Component() {
  const { facilityKey } = Route.useParams();

  const facilityQuery = useFacilityQuery(facilityKey);
  const { data: facility } = facilityQuery;
  const importing = useRef(false);
  const router = useRouter();
  const translate = useTranslate();
  const [files, setFiles] = useState<Array<File>>([]);
  const [progress, setProgress] = useState(100);

  const headerRows = useMemo(
    function () {
      return SimpleIndependenceLevelBatchRegistrationHeaderRows({ translate });
    },
    [translate],
  );

  const schema = SimpleIndependenceLevelBatchRegistrationRowSchema();

  const residentInfiniteQuery = useResidentInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (residentInfiniteQuery.hasNextPage && !residentInfiniteQuery.isFetching) {
        residentInfiniteQuery.fetchNextPage();
      }
    },
    [residentInfiniteQuery],
  );

  const residents = useMemo(
    function () {
      return (
        residentInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.residents;
        }) ?? []
      );
    },
    [residentInfiniteQuery.data],
  );

  const dialogClasses = DialogStyle();
  const progressClasses = progressStyles();

  return (
    <main
      className={css({
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "32px 24px" },
      })}
    >
      <PageName>
        {facility
          ? translate("%SimpleIndependenceLevelBatchRegistration.0%") + " - " + facility.name
          : translate("%Loading.0%")}
      </PageName>

      <Dialog.Root open={progress !== 100}>
        <Portal>
          <Dialog.Backdrop className={dialogClasses.backdrop} />
          <Dialog.Positioner className={dialogClasses.positioner}>
            <Dialog.Content asChild>
              <Progress.Root className={progressClasses.root} value={progress}>
                <Progress.Label className={progressClasses.label}>
                  {translate("%Processing.0%")}
                </Progress.Label>
                <Progress.Track className={progressClasses.track}>
                  <Progress.Range className={progressClasses.range} />
                </Progress.Track>
                <Progress.ValueText className={progressClasses.valueText} />
              </Progress.Root>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <div
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          maxWidth: "480px",
          padding: { smDown: "16px", sm: "16px 24px" },
          width: "stretch",
        })}
      >
        <h1
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {translate("%SimpleIndependenceLevelBatchRegistration.1%")}
        </h1>
        <p className={TypographyStyle({ fontSize: "14px", marginTop: "16px" })}>
          {translate("%SimpleIndependenceLevelBatchRegistration.2%")}
        </p>

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          1.&nbsp;{translate("%Download.1%")}
        </h2>
        <div className={css({ marginTop: "8px" })}>
          <button
            className={ButtonStyle({
              appearance: "contained",
              borderRadius: "4px",
              color: "secondary",
              flexGrow: 1,
              fontSize: "14px",
              gap: "8px",
              padding: "10px 16px",
              width: "stretch",
            })}
            disabled={residentInfiniteQuery.isFetching}
            onClick={async function () {
              saveAsFile(
                new Blob(
                  [
                    new Uint8Array(
                      Encoding.convert(
                        Encoding.stringToCode(
                          headerRows
                            .concat(
                              residents.map(function (resident) {
                                return toSimpleIndependenceLevelBatchRegistrationRow(resident);
                              }),
                            )
                            .join("\r\n"),
                        ),
                        { from: "UNICODE", to: "SJIS" },
                      ),
                    ),
                  ],
                  { type: "text/csv" },
                ),
                translate("%SimpleIndependenceLevelBatchRegistration.3%", {
                  $: { facility, now: Temporal.Now.plainDateTimeISO("Asia/Tokyo") },
                }) + ".csv",
              );
            }}
          >
            <LuDownload />
            <span>{translate("%Download.0%")}</span>
          </button>
        </div>

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          2.&nbsp;{translate("%Upload.1%")}
        </h2>

        <div className={css({ marginTop: "8px" })}>
          <FileUpload.Root
            acceptedFiles={files}
            className={fileUploadStyle.root}
            maxFiles={3}
            onFileChange={function ({ acceptedFiles }) {
              setFiles(acceptedFiles);
            }}
          >
            <FileUpload.Dropzone className={fileUploadStyle.dropzone}>
              <span className={css({ pointerEvents: "none" })}>{translate("%DragAndDrop.0%")}</span>
              <span className={css({ pointerEvents: "none" })}>{translate("%Or.0%")}</span>
              <span className={css({ pointerEvents: "none" })}>{translate("%Select.1%")}</span>
            </FileUpload.Dropzone>
            <FileUpload.ItemGroup className={fileUploadStyle.itemGroup}>
              <FileUpload.Context>
                {function ({ acceptedFiles }) {
                  return acceptedFiles.map(function (file, index) {
                    return (
                      <FileUpload.Item className={fileUploadStyle.item} file={file} key={index}>
                        <FileUpload.ItemPreview type="image/*">
                          <FileUpload.ItemPreviewImage />
                        </FileUpload.ItemPreview>
                        <FileUpload.ItemPreview type=".*">
                          <LuFile className={fileUploadStyle.itemPreviewIcon} />
                        </FileUpload.ItemPreview>
                        <FileUpload.ItemName className={fileUploadStyle.itemName} />
                        <FileUpload.ItemDeleteTrigger className={fileUploadStyle.itemDeleteTrigger}>
                          <LuTrash />
                        </FileUpload.ItemDeleteTrigger>
                      </FileUpload.Item>
                    );
                  });
                }}
              </FileUpload.Context>
            </FileUpload.ItemGroup>
            <FileUpload.HiddenInput />
          </FileUpload.Root>
        </div>

        <div className={css({ display: "flex", gap: "8px", marginTop: "16px" })}>
          <Link
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              flexGrow: 1,
              fontSize: "14px",
              padding: "10px 16px",
            })}
            onClick={function (event) {
              if (router.history.canGoBack()) {
                event.preventDefault();
                router.history.back();
              }
            }}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/residents"
          >
            {translate("%Cancel.0%")}
          </Link>
          <button
            className={ButtonStyle({
              appearance: "contained",
              borderRadius: "4px",
              color: "primary",
              flexGrow: 1,
              fontSize: "14px",
              padding: "10px 16px",
            })}
            disabled={files.length === 0}
            onClick={async function () {
              if (importing.current) {
                return;
              }

              importing.current = true;

              for (let f = 0; f < files.length; f++) {
                setProgress(0);

                const file = files[f];
                let rows;
                try {
                  const bytes = new Uint8Array(await file.arrayBuffer());
                  const t = Encoding.convert(bytes, { from: "SJIS", to: "UNICODE" });
                  const text = Encoding.codeToString(t) as string;
                  rows = text.split(/\r\n|\n|\r/).filter(function (row) {
                    return row.trim().length > 0;
                  });
                } catch (error) {
                  console.error(error);
                  showMessage({
                    body: translate("%Import.1%", { $: { file } }),
                    color: "error",
                    title: translate("%Error.0%"),
                    icon: undefined,
                    type: "toast",
                  });
                  continue;
                }

                console.info("ファイルを読み込みました。", rows);

                let assessments: Array<{
                  index: number;
                  simpleIndependenceLevels: SimpleIndependenceLevels1;
                  userKey: UserKey;
                }>;
                try {
                  assessments = await lastValueFrom(
                    from(rows).pipe(
                      mergeMap(async function (row, index) {
                        if (index < headerRows.length) {
                          // ヘッダ行はスキップする
                          return undefined;
                        }

                        let data;
                        try {
                          data = v.parse(schema, row.split(","));
                        } catch (error) {
                          console.error(error);
                          throw new Error(translate("%Import.2%", { $: { file, row: index + 1 } }));
                        }

                        const {
                          simpleIndependenceLevelI,
                          simpleIndependenceLevelL,
                          simpleIndependenceLevelQ,
                          simpleIndependenceLevelR,
                        } = data;

                        if (
                          !simpleIndependenceLevelI &&
                          !simpleIndependenceLevelL &&
                          !simpleIndependenceLevelQ &&
                          !simpleIndependenceLevelR
                        ) {
                          // 全ての自立度の列が空の場合、スキップする
                          return;
                        } else if (
                          !simpleIndependenceLevelI ||
                          !simpleIndependenceLevelL ||
                          !simpleIndependenceLevelQ ||
                          !simpleIndependenceLevelR
                        ) {
                          // 自立度の列が全て埋まっていない場合、エラーとする。
                          throw new Error(translate("%Import.2%", { $: { file, row: index + 1 } }));
                        }

                        const userKey =
                          data.userKey ??
                          residents
                            .filter(function (resident) {
                              return resident.name === data.name;
                            })
                            .at(0)?.userKey;

                        if (!userKey) {
                          throw new Error(
                            translate("%SimpleIndependenceLevelBatchRegistration.6%", {
                              $: { name: data.name },
                            }),
                          );
                        }

                        return {
                          index,
                          simpleIndependenceLevels: {
                            [FIM_ITEM.BED_CHAIR_WHEELCHAIR]: simpleIndependenceLevelI,
                            [FIM_ITEM.WALK_WHEELCHAIR]: simpleIndependenceLevelL,
                            [FIM_ITEM.PROBLEM_SOLVING]: simpleIndependenceLevelQ,
                            [FIM_ITEM.MEMORY]: simpleIndependenceLevelR,
                          },
                          userKey,
                        } as const;
                      }, 8),
                      filter(function (assessment) {
                        return assessment !== undefined;
                      }),
                      toArray(),
                      map(function (assessments) {
                        // 行の順番を復元する。
                        return assessments.sort(function ({ index: a }, { index: b }) {
                          return a - b;
                        });
                      }),
                    ),
                  );
                } catch (error) {
                  if (error instanceof EmptyError) {
                    assessments = [];
                  } else {
                    if (error instanceof Error) {
                      showMessage({
                        body: error.message,
                        color: "error",
                        title: translate("%Error.0%"),
                        icon: undefined,
                        type: "toast",
                      });
                    }
                    continue;
                  }
                }

                try {
                  await registerIndependenceLevels({
                    independenceLevelEntries: assessments,
                    type: INDEPENDENCE_LEVEL_TYPE.COMPLEMENTED,
                  });
                } catch (error) {
                  console.error(error);
                  showMessage({
                    body: translate("%Import.3%", { $: { file } }),
                    color: "error",
                    title: translate("%Error.0%"),
                    icon: undefined,
                    type: "toast",
                  });

                  setProgress(100);
                  return;
                }

                showMessage({
                  body: translate("%Import.6%", { $: { file } }),
                  color: "success",
                  title: translate("%Success.0%"),
                  icon: undefined,
                  type: "toast",
                });
              }

              removeIndependenceLevelEntryInfiniteQueryCache();

              setProgress(100);

              setFiles([]);

              importing.current = false;
            }}
          >
            {translate("%Import.0%")}
          </button>
        </div>
      </div>
    </main>
  );
}
